import { Link } from "@inertiajs/react"

export interface EvidenceTypeOption {
  id: number
  name: string
  media_kind: string | null
  sort_order: number
}

export interface EntryFormData {
  entry_type: number
  start_time: string
  entry_text: string
  evidence_type_ids: number[]
  significant: number
}

interface Props {
  caseId: number
  reportId: number
  data: EntryFormData
  setData: (
    key: keyof EntryFormData,
    value: EntryFormData[keyof EntryFormData],
  ) => void
  errors?: Record<string, string[]>
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  submitLabel: string
  evidenceTypes: EvidenceTypeOption[]
}

export default function EntryForm({
  caseId,
  reportId,
  data,
  setData,
  errors,
  processing,
  onSubmit,
  submitLabel,
  evidenceTypes,
}: Props) {
  const entryTypes = [
    { value: 0, label: "Original" },
    { value: 1, label: "Revision" },
    { value: 10, label: "Final" },
  ]

  const toggleEvidenceType = (id: number) => {
    setData(
      "evidence_type_ids",
      data.evidence_type_ids.includes(id)
        ? data.evidence_type_ids.filter((x) => x !== id)
        : [...data.evidence_type_ids, id],
    )
  }

  return (
    <form onSubmit={onSubmit} className="max-w-3xl">
      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="entry_type" className="mb-1 block font-medium">
            Entry Type
          </label>
          <select
            id="entry_type"
            value={data.entry_type}
            onChange={(e) => setData("entry_type", parseInt(e.target.value))}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          >
            {entryTypes.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          {errors?.entry_type && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.entry_type.join(", ")}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="start_time" className="mb-1 block font-medium">
            Start Time
          </label>
          <input
            type="datetime-local"
            id="start_time"
            step="1"
            value={data.start_time ? data.start_time.slice(0, 19) : ""}
            onChange={(e) => setData("start_time", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          />
          {errors?.start_time && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.start_time.join(", ")}
            </p>
          )}
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="entry_text" className="mb-1 block font-medium">
          Entry Text
        </label>
        <textarea
          id="entry_text"
          value={data.entry_text}
          onChange={(e) => setData("entry_text", e.target.value)}
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
          rows={8}
        />
        {errors?.entry_text && (
          <p className="text-hpi-red mt-1 text-sm">
            {errors.entry_text.join(", ")}
          </p>
        )}
      </div>

      <div className="mb-4">
        <h3 className="mb-2 font-medium">Evidence</h3>
        {evidenceTypes.length > 0 ? (
          <div className="grid gap-3 md:grid-cols-4">
            {evidenceTypes.map((type) => (
              <label key={type.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={data.evidence_type_ids.includes(type.id)}
                  onChange={() => toggleEvidenceType(type.id)}
                  className="border-hpi-light-gray rounded"
                />
                <span>{type.name}</span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-hpi-med-gray text-sm">
            No evidence types configured. Add some in Admin → Evidence Types.
          </p>
        )}
      </div>

      <div className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={data.significant === 1}
            onChange={(e) => setData("significant", e.target.checked ? 1 : 0)}
            className="border-hpi-light-gray rounded"
          />
          <span className="font-medium">Significant</span>
        </label>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <Link
          href={`/cases/${caseId}/reports/${reportId}`}
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
